import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
  DialogTitle,
} from "@/components/ui/dialog";
import { Play, Clock, Users, Mountain, MapPin, ChevronDown, Globe, Shield, Phone, Mail, Sparkles, CalendarDays } from "lucide-react";
import { useLang } from "@/i18n";
import axios from "axios";
import FadeImage from "@/components/FadeImage";
import Reveal from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PROMO_VIDEO = "/assets/promo-video.mp4";

const HERO_BG = "/assets/sfondo-website.webp";
const TOUR_SINGLE_IMG = "/assets/tour-singolo.webp";
const TOUR_DOUBLE_IMG = "/assets/tour-doppio.webp";
const TOUR_MOTTARONE_IMG = "/assets/tour-mottarone.webp";
const TEAM_PHOTO = "/assets/chi-siamo.webp";

export default function HomePage() {
  const navigate = useNavigate();
  const { t, toggleLang, lang } = useLang();
  const [videoOpen, setVideoOpen] = useState(false);
  const [mottarone, setMottarone] = useState(null);
  const [toursCfg, setToursCfg] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tours/config`)
      .then((res) => {
        if (!cancelled && res?.data && typeof res.data === "object") setToursCfg(res.data);
      })
      .catch(() => { /* fallback ai valori statici */ });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/mottarone/info`)
      .then((res) => {
        if (!cancelled && res?.data && typeof res.data === "object") {
          setMottarone(res.data);
        }
      })
      .catch(() => { /* card usa i valori di default */ });
    return () => { cancelled = true; };
  }, []);

  const mottaroneDates = Array.isArray(mottarone?.dates) ? mottarone.dates : [];
  const nextMottaroneDate = mottaroneDates.length > 0 ? mottaroneDates[0] : null;
  const mottaronePriceSingle = Number(nextMottaroneDate?.price_single) || 200;
  const mottaronePriceDouble = Number(nextMottaroneDate?.price_double) || 280;
  const mottaroneDesc =
    (lang === "it" ? mottarone?.settings?.description_it : mottarone?.settings?.description_en) ||
    t("tourMottaroneDesc");

  const tours = [
    {
      type: "single",
      nameKey: "tourSingleName",
      price: Number(toursCfg?.single?.price) || 115,
      desc: (lang === "it" ? toursCfg?.single?.description_it : toursCfg?.single?.description_en) || null,
      personsKey: "tourSinglePersons",
      descKey: "tourSingleDesc",
      img: TOUR_SINGLE_IMG,
    },
    {
      type: "double",
      nameKey: "tourDoubleName",
      price: Number(toursCfg?.double?.price) || 165,
      desc: (lang === "it" ? toursCfg?.double?.description_it : toursCfg?.double?.description_en) || null,
      personsKey: "tourDoublePersons",
      descKey: "tourDoubleDesc",
      img: TOUR_DOUBLE_IMG,
    },
  ];

  const scrollToTours = () => {
    document.getElementById("tours-section")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-[#0a1a0f]">
      {/* VibeTech Studio Top Bar */}
      <div className="py-2 px-4 flex items-center justify-center gap-1.5" data-testid="vibetech-topbar">
        <span className="text-[#3a5a3a] text-[10px] tracking-widest uppercase">Powered & Developed by</span>
        <span className="text-[#5a7a5a] text-[10px] tracking-widest uppercase font-bold">VibeTech Studio</span>
      </div>

      {/* Language Toggle - Fixed */}
      <button
        data-testid="lang-toggle"
        onClick={toggleLang}
        className="fixed top-4 right-4 z-50 lang-toggle flex items-center gap-1.5 px-3 py-1.5 rounded-full glass-card text-sm font-semibold text-[#d4a056] hover:text-white transition-colors"
      >
        <Globe className="w-3.5 h-3.5" />
        {t("langToggle")}
      </button>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-end" data-testid="hero-section">
        <div className="absolute inset-0">
          <FadeImage src={HERO_BG} alt="Quad Lago Maggiore" wrapperClassName="w-full h-full" className="w-full h-full object-cover" style={{ objectPosition: "center 60%" }} loading="eager" fetchpriority="high" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0a1a0f] via-[#0a1a0f]/60 to-[#0a1a0f]/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 md:pb-24 w-full">
          <div className="flex items-center gap-3 mb-4 animate-slide-up">
            <Mountain className="w-5 h-5 text-[#d4a056]" />
            <span className="text-sm font-bold uppercase tracking-widest text-[#d4a056]">
              {t("heroTag")}
            </span>
          </div>

          <h1
            className="text-5xl md:text-7xl lg:text-8xl font-bold uppercase tracking-tighter text-[#e8f0e8] mb-4 leading-[0.9] animate-slide-up-delay-1 relative"
            data-testid="hero-title"
          >
            {t("heroTitle1")}
            <br />
            <span className="gradient-text text-glow">{t("heroTitle2")}</span>
            <br />
            <span className="text-[#d4a056] text-glow-gold text-4xl md:text-5xl lg:text-6xl">{t("heroTitle3")}</span>
          </h1>

          <p className="text-base md:text-lg text-white/80 max-w-xl mb-8 leading-relaxed animate-slide-up-delay-2">
            {t("heroSubtitle")}
          </p>

          <p className="text-sm md:text-base italic text-[#d4a056] max-w-lg mb-8 animate-slide-up-delay-3" data-testid="hero-slogan">
            {lang === "it"
              ? "\"Il Lago Maggiore non si visita, si conquista. Guida il tuo gruppo, libera il tuo Spirito.\""
              : "\"Lake Maggiore is not visited, it is conquered. Lead your group, free your Spirit.\""}
          </p>

          {/* Scroll indicator */}
          <button
            onClick={scrollToTours}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 text-[#8faa8f] hover:text-[#2d7a4a] transition-colors animate-bounce"
            data-testid="scroll-down-btn"
          >
            <ChevronDown className="w-6 h-6" />
          </button>
        </div>
      </section>

      {/* Tours Section */}
      <section
        id="tours-section"
        className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto"
        data-testid="tours-section"
      >
        {/* Desktop: 3-column row (Play | Title | Logo). Mobile: stacked vertically */}
        <div className="mb-12">
          <div className="flex flex-col items-center gap-6 md:flex-row md:items-center md:justify-between">

            {/* LEFT: Play Video Button */}
            <div className="order-3 md:order-1 md:flex-1 flex justify-center">
              <Dialog open={videoOpen} onOpenChange={setVideoOpen}>
                <DialogTrigger asChild>
                  <button
                    data-testid="play-video-btn"
                    className="flex flex-col items-center gap-2 group/play"
                  >
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-[#d4a056]/90 rounded-full flex items-center justify-center hover:bg-[#e0b86e] transition-all shadow-lg shadow-[#d4a056]/20 cursor-pointer">
                      <Play className="w-8 h-8 md:w-10 md:h-10 text-[#0a1a0f] ml-1 group-hover/play:scale-110 transition-transform" />
                    </div>
                    <span className="text-white text-sm md:text-base font-bold uppercase tracking-wider">
                      Tour Preview
                    </span>
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#0a1a0f] border-[#1a4a2e] max-w-4xl p-0 rounded-xl overflow-hidden">
                  <DialogTitle className="sr-only">Video Tour</DialogTitle>
                  <div className="aspect-video">
                    {videoOpen && (
                      <video
                        src={PROMO_VIDEO}
                        className="w-full h-full object-cover"
                        controls
                        autoPlay
                        playsInline
                        data-testid="promo-video-player"
                      />
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* CENTER: Title */}
            <div className="order-2 md:order-2 md:flex-1 text-center">
              <div className="flex items-center justify-center gap-3 mb-2">
                <MapPin className="w-4 h-4 text-[#2d7a4a]" />
                <span className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a]">
                  {t("toursTag")}
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold uppercase tracking-tight text-white mb-3">
                {t("toursTitle1")}
                <br />
                <span className="gradient-text">{t("toursTitle2")}</span>
              </h2>
              <p className="flex items-center justify-center gap-2 text-sm text-[#d4a056]">
                <Shield className="w-4 h-4" /> {t("minAge")}
              </p>
            </div>

            {/* RIGHT: Logo */}
            <div className="order-1 md:order-3 md:flex-1 flex justify-center">
              <img
                src="/assets/logo-naskide-new.webp"
                alt="Spirito Libero Lago Maggiore"
                className="w-48 md:w-56 lg:w-64 h-auto"
                style={{ filter: "drop-shadow(0 4px 16px rgba(0,0,0,0.5))" }}
                data-testid="tours-logo"
              />
            </div>

          </div>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">

          {tours.map((tour, idx) => (
            <Reveal key={tour.type} delay={idx * 120}>
            <div
              data-testid={`tour-card-${tour.type}`}
              className="glass-card rounded-xl hover:border-[#2d7a4a]/40 transition-all group relative overflow-hidden glow-hover h-full"
            >
              <div className="aspect-[4/3] overflow-hidden rounded-t-xl">
                <FadeImage
                  src={tour.img}
                  alt={t(tour.nameKey)}
                  wrapperClassName="w-full h-full"
                  className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                  decoding="async"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-2xl md:text-3xl font-bold uppercase text-white">
                    {t(tour.nameKey)}
                  </h3>
                  <span className="text-3xl md:text-4xl font-bold text-[#d4a056] text-glow-gold">
                    €{tour.price}
                  </span>
                </div>
                <p className="text-[#c0d4c0] text-sm mb-4">
                  {tour.desc || t(tour.descKey)}
                </p>
                <div className="flex gap-4 mb-6 text-[#c0d4c0] text-sm">
                  <span className="flex items-center gap-1.5">
                    <Users className="w-4 h-4 text-[#2d7a4a]" /> {t(tour.personsKey)}
                  </span>
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#2d7a4a]" /> {t("duration")}
                  </span>
                </div>
                <button
                  data-testid={`book-${tour.type}-card-btn`}
                  onClick={() => navigate(`/prenota/${tour.type}`)}
                  className="w-full bg-[#2d7a4a] text-white font-bold uppercase tracking-wider py-4 md:py-3 hover:bg-[#3a9960] transition-all active:scale-[0.97] text-base md:text-sm rounded-lg min-h-[52px]"
                >
                  {t("bookNow")}
                </button>
              </div>
            </div>
            </Reveal>
          ))}

          {/* Tour Mottarone - Special Card (su richiesta) */}
          <Reveal delay={240} className="md:col-span-2 lg:col-span-1">
          <div
            data-testid="tour-card-mottarone"
            className="rounded-xl transition-all group relative overflow-hidden border border-[#d4a056]/50 bg-[#0f2518]/60 backdrop-blur-sm hover:border-[#d4a056] shadow-lg shadow-[#d4a056]/10 h-full"
          >
            <div className="absolute top-3 left-3 z-10 flex items-center gap-1.5 bg-[#d4a056] text-[#0a1a0f] text-[10px] font-bold uppercase tracking-widest px-3 py-1.5 rounded-full shadow-md">
              <Sparkles className="w-3 h-3" /> {t("mottaroneBadge")}
            </div>
            <div className="aspect-[4/3] overflow-hidden rounded-t-xl">
              <FadeImage
                src={TOUR_MOTTARONE_IMG}
                alt={t("tourMottaroneName")}
                wrapperClassName="w-full h-full"
                className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-500"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="p-6">
              <h3 className="text-2xl md:text-3xl font-bold uppercase text-[#d4a056] text-glow-gold mb-3">
                {t("tourMottaroneName")}
              </h3>
              <div className="flex items-baseline gap-x-4 gap-y-1 flex-wrap mb-3">
                <span className="text-2xl font-bold text-[#d4a056]">
                  €{mottaronePriceSingle}
                  <span className="text-[10px] text-[#8faa8f] uppercase font-normal ml-1">
                    {lang === "it" ? "singolo" : "single"}
                  </span>
                </span>
                <span className="text-2xl font-bold text-[#d4a056]">
                  €{mottaronePriceDouble}
                  <span className="text-[10px] text-[#8faa8f] uppercase font-normal ml-1">
                    {lang === "it" ? "doppio" : "double"}
                  </span>
                </span>
              </div>
              <p className="text-[#c0d4c0] text-sm mb-4">
                {mottaroneDesc}
              </p>
              <div className="flex gap-4 mb-6 text-[#c0d4c0] text-sm flex-wrap">
                <span className="flex items-center gap-1.5">
                  <CalendarDays className="w-4 h-4 text-[#d4a056]" /> {t("mottaroneOnlyDates")}
                </span>
                {nextMottaroneDate?.duration && (
                  <span className="flex items-center gap-1.5">
                    <Clock className="w-4 h-4 text-[#d4a056]" /> {nextMottaroneDate.duration}
                  </span>
                )}
              </div>
              <button
                data-testid="book-mottarone-card-btn"
                onClick={() => navigate("/prenota/mottarone")}
                className="w-full bg-[#d4a056] text-[#0a1a0f] font-bold uppercase tracking-wider py-4 md:py-3 hover:bg-[#e0b86e] transition-all active:scale-[0.97] text-base md:text-sm rounded-lg min-h-[52px]"
              >
                {nextMottaroneDate ? t("bookNow") : (lang === "it" ? "SCOPRI LE DATE" : "SEE DATES")}
              </button>
            </div>
          </div>
          </Reveal>
        </div>

        {/* Google Reviews Banner */}
        <Reveal>
        <div
          data-testid="google-reviews-banner"
          className="mt-10 glass-card rounded-xl px-6 py-5 flex flex-col sm:flex-row items-center justify-between gap-4 border border-[#1a4a2e] hover:border-[#d4a056]/40 transition-colors"
        >
          <div className="flex items-center gap-4">
            <svg width="40" height="40" viewBox="0 0 48 48" aria-label="Google">
              <path fill="#FFC107" d="M43.6 20.1H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.3 4 24 4 13 4 4 13 4 24s9 20 20 20 20-9 20-20c0-1.3-.1-2.6-.4-3.9z"/>
              <path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3l5.7-5.7C34.2 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"/>
              <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"/>
              <path fill="#1976D2" d="M43.6 20.1H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C41 35.4 44 30.2 44 24c0-1.3-.1-2.6-.4-3.9z"/>
            </svg>
            <div>
              <div className="flex items-center gap-1 mb-1" data-testid="google-reviews-stars">
                {[0, 1, 2, 3, 4].map((i) => (
                  <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#FBBC04">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                ))}
              </div>
              <p className="text-white font-semibold text-sm">
                {lang === "it" ? "I nostri clienti ci premiano su Google" : "Our guests rate us on Google"}
              </p>
              <p className="text-[#8faa8f] text-xs">
                {lang === "it"
                  ? "Recensioni verificate dei tour Spirito Libero Lago Maggiore"
                  : "Verified reviews of Spirito Libero Lago Maggiore tours"}
              </p>
            </div>
          </div>
          <a
            href="https://www.google.com/maps/search/?api=1&query=Spirito+Libero+Lago+Maggiore+quad+Nebbiuno"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="google-reviews-link"
            className="flex-shrink-0 border-2 border-[#d4a056] text-[#d4a056] font-bold uppercase tracking-wider px-6 py-3 hover:bg-[#d4a056] hover:text-[#0a1a0f] transition-all text-xs rounded-lg"
          >
            {lang === "it" ? "Leggi le Recensioni" : "Read the Reviews"}
          </a>
        </div>
        </Reveal>
      </section>

      {/* Team / About Section */}
      <section className="py-16 md:py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="team-section">
        <div className="grid md:grid-cols-2 gap-10 items-start">
          <Reveal>
          <div className="rounded-xl overflow-hidden glow-hover">
            <FadeImage
              src={TEAM_PHOTO}
              alt="Il nostro team"
              wrapperClassName="w-full"
              className="w-full h-auto max-h-[500px] object-cover object-center rounded-xl"
              loading="lazy"
              decoding="async"
              data-testid="team-photo"
            />
          </div>
          </Reveal>
          <Reveal delay={120}>
            <div className="flex items-center gap-3 mb-3">
              <Users className="w-4 h-4 text-[#2d7a4a]" />
              <span className="text-sm font-bold uppercase tracking-widest text-[#2d7a4a]">
                {lang === "it" ? "Chi Siamo" : "About Us"}
              </span>
            </div>
            <h2 className="text-3xl md:text-4xl font-bold uppercase tracking-tight text-white mb-2">
              {lang === "it" ? "L'ANIMA DI" : "THE SOUL OF"}
              <br />
              <span className="gradient-text">{lang === "it" ? "SPIRITO LIBERO" : "SPIRITO LIBERO"}</span>
            </h2>

            <p className="text-white/80 leading-relaxed mb-5 text-sm">
              {lang === "it"
                ? "Spirito Libero Lago Maggiore non è solo un noleggio quad: è il punto d'incontro tra la natura selvaggia del nostro territorio e la voglia di scoperta che appartiene a ognuno di noi. Nata dalla passione e dall'esperienza decennale di Naskide Antonioli, la nostra realtà è cresciuta con un obiettivo chiaro: permettere a chiunque — dalle famiglie con bambini ai gruppi di amici in cerca di adrenalina — di vivere il lato \"offroad\" della vita in totale sicurezza."
                : "Spirito Libero Lago Maggiore is more than a quad rental: it's where the wild nature of our territory meets the spirit of discovery in all of us. Born from the passion and decade-long experience of Naskide Antonioli, we grew with one clear goal: to let everyone — from families with kids to thrill-seeking groups — experience the offroad side of life in total safety."}
            </p>

            <h3 className="text-lg font-bold text-[#d4a056] uppercase tracking-wider mb-3">
              {lang === "it" ? "Perché Sceglierci?" : "Why Choose Us?"}
            </h3>
            <div className="space-y-3 mb-6">
              <div className="flex gap-3">
                <Shield className="w-5 h-5 text-[#2d7a4a] flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm">
                  <span className="text-white font-semibold">{lang === "it" ? "Guida Esperta:" : "Expert Guide:"}</span>{" "}
                  {lang === "it"
                    ? "Con Naskide (\"Naski\") al tuo fianco, non sarai mai solo. La sua presenza costante garantisce che ogni pilota trovi il proprio ritmo e superi ogni sfida con il sorriso."
                    : "With Naskide (\"Naski\") by your side, you'll never be alone. His constant presence ensures every rider finds their rhythm and conquers every challenge with a smile."}
                </p>
              </div>
              <div className="flex gap-3">
                <MapPin className="w-5 h-5 text-[#2d7a4a] flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm">
                  <span className="text-white font-semibold">{lang === "it" ? "Territorio Autentico:" : "Authentic Territory:"}</span>{" "}
                  {lang === "it"
                    ? "Ti portiamo dove l'asfalto finisce e inizia la vera bellezza. Dai boschi dell'Alto Vergante agli scorci mozzafiato sulla Rocca di Angera."
                    : "We take you where the asphalt ends and true beauty begins. From the forests of Alto Vergante to the breathtaking views of Rocca di Angera."}
                </p>
              </div>
              <div className="flex gap-3">
                <Mountain className="w-5 h-5 text-[#2d7a4a] flex-shrink-0 mt-0.5" />
                <p className="text-white/75 text-sm">
                  <span className="text-white font-semibold">{lang === "it" ? "Sicurezza e Carisma:" : "Safety & Charisma:"}</span>{" "}
                  {lang === "it"
                    ? "La vera libertà nasce dalla tranquillità. I nostri quad sono ai massimi standard e i briefing iniziali ti danno tutto il controllo necessario."
                    : "True freedom comes from peace of mind. Our quads meet the highest standards and our initial briefings give you all the control you need."}
                </p>
              </div>
            </div>

            <p className="text-[#d4a056] italic text-sm mb-6">
              {lang === "it"
                ? "\"Sali a bordo, impugna il manubrio e diventa il protagonista della tua storia.\""
                : "\"Climb aboard, grab the handlebars and become the protagonist of your own story.\""}
            </p>

            <button
              data-testid="book-from-team"
              onClick={() => document.getElementById("tours-section")?.scrollIntoView({ behavior: "smooth", block: "center" })}
              className="bg-[#2d7a4a] text-white font-bold uppercase tracking-wider px-8 py-4 md:py-3 hover:bg-[#3a9960] transition-all active:scale-95 text-base md:text-sm rounded-lg min-h-[52px]"
            >
              {t("bookNow")}
            </button>
          </Reveal>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#1a4a2e]">
        {/* Contatti */}
        <div className="max-w-7xl mx-auto px-4 py-10" data-testid="contacts-section">
          <div className="grid sm:grid-cols-3 gap-8">
            {/* Brand */}
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Mountain className="w-4 h-4 text-[#2d7a4a]" />
                <span className="font-bold text-white uppercase text-sm">
                  {t("brand")} <span className="text-[#d4a056]">{t("brandSub")}</span>
                </span>
              </div>
              <p className="text-[#8faa8f] text-xs leading-relaxed">
                {lang === "it"
                  ? "Escursioni in quad tra i sentieri panoramici del Lago Maggiore."
                  : "Quad excursions along the scenic trails of Lake Maggiore."}
              </p>
            </div>

            {/* Contatti */}
            <div>
              <h4 className="text-[#2d7a4a] text-xs font-bold uppercase tracking-widest mb-3">
                {lang === "it" ? "Contatti" : "Contacts"}
              </h4>
              <div className="space-y-2 text-sm">
                <p className="text-white font-semibold">Naskide Antonioli</p>
                <p className="text-[#8faa8f] flex items-center gap-2">
                  <Phone className="w-3.5 h-3.5 text-[#2d7a4a]" /> +39 333 793 0166
                </p>
                <p className="text-[#8faa8f] flex items-center gap-2">
                  <Mail className="w-3.5 h-3.5 text-[#2d7a4a]" /> spiritolibero.bynaski@gmail.com
                </p>
              </div>
            </div>

            {/* Link Legali */}
            <div>
              <h4 className="text-[#2d7a4a] text-xs font-bold uppercase tracking-widest mb-3">
                {lang === "it" ? "Informazioni Legali" : "Legal"}
              </h4>
              <div className="space-y-2 text-sm">
                <a
                  href="/termini-e-condizioni"
                  data-testid="terms-link"
                  className="text-[#8faa8f] hover:text-[#d4a056] transition-colors block"
                >
                  {lang === "it" ? "Termini e Condizioni" : "Terms & Conditions"}
                </a>
                <a
                  href="/privacy-policy"
                  data-testid="privacy-link"
                  className="text-[#8faa8f] hover:text-[#d4a056] transition-colors block"
                >
                  Privacy Policy
                </a>
                <a
                  href="/cookie-policy"
                  data-testid="cookie-policy-link"
                  className="text-[#8faa8f] hover:text-[#d4a056] transition-colors block"
                >
                  Cookie Policy
                </a>
                <button
                  data-testid="manage-cookies-btn"
                  onClick={() => window.dispatchEvent(new Event("openCookieSettings"))}
                  className="text-[#8faa8f] hover:text-[#d4a056] transition-colors block text-left"
                >
                  {lang === "it" ? "Gestisci Cookie" : "Manage Cookies"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="border-t border-[#1a4a2e] py-6 px-4">
          <div className="max-w-7xl mx-auto flex flex-col items-center gap-3">
            <span className="text-[#2d7a4a] text-xs font-bold uppercase tracking-widest">
              {lang === "it" ? "Seguici" : "Follow Us"}
            </span>
            <div className="flex items-center gap-5">
              <a
                href="https://www.facebook.com/naschiderentquad"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-facebook"
                className="transition-transform hover:scale-110"
                aria-label="Facebook"
              >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                  <circle cx="12" cy="12" r="12" fill="#1877F2"/>
                  <path d="M16.67 13.13l.38-2.47h-2.37V9.13c0-.68.33-1.34 1.4-1.34h1.08V5.68a13.2 13.2 0 00-1.92-.17c-1.96 0-3.24 1.19-3.24 3.34v1.88H9.88v2.47h1.72V19h2.18v-5.87h1.89z" fill="#fff"/>
                </svg>
              </a>
              <a
                href="https://www.instagram.com/spiritolibero.bynaski"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-instagram"
                className="transition-transform hover:scale-110"
                aria-label="Instagram"
              >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                  <defs>
                    <radialGradient id="ig" cx="30%" cy="107%" r="150%">
                      <stop offset="0%" stopColor="#fdf497"/>
                      <stop offset="5%" stopColor="#fdf497"/>
                      <stop offset="45%" stopColor="#fd5949"/>
                      <stop offset="60%" stopColor="#d6249f"/>
                      <stop offset="90%" stopColor="#285AEB"/>
                    </radialGradient>
                  </defs>
                  <rect width="24" height="24" rx="6" fill="url(#ig)"/>
                  <rect x="4.5" y="4.5" width="15" height="15" rx="4" stroke="#fff" strokeWidth="1.5" fill="none"/>
                  <circle cx="12" cy="12" r="3.5" stroke="#fff" strokeWidth="1.5" fill="none"/>
                  <circle cx="17" cy="7" r="1.2" fill="#fff"/>
                </svg>
              </a>
              <a
                href="https://www.tiktok.com/@naskispiritolibero"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-tiktok"
                className="transition-transform hover:scale-110"
                aria-label="TikTok"
              >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="6" fill="#010101"/>
                  <path d="M16.6 8.2a3.5 3.5 0 01-2.1-.7 3.5 3.5 0 01-1.2-1.8h-2v9.1a2.1 2.1 0 01-2.1 2 2.1 2.1 0 01-2.1-2.1 2.1 2.1 0 012.1-2.1c.2 0 .5 0 .7.1V10.5a4.3 4.3 0 00-.7-.1 4.2 4.2 0 00-4.2 4.3 4.2 4.2 0 004.2 4.2 4.2 4.2 0 004.2-4.2V10a5.5 5.5 0 003.2 1V8.8a3.5 3.5 0 01-.1-.6z" fill="#25F4EE"/>
                  <path d="M17.2 8.2a3.5 3.5 0 01-2.1-.7 3.5 3.5 0 01-1.2-1.8h-2v9.1a2.1 2.1 0 01-2.1 2 2.1 2.1 0 01-2.1-2.1 2.1 2.1 0 012.1-2.1c.2 0 .5 0 .7.1V10.5a4.3 4.3 0 00-.7-.1 4.2 4.2 0 00-4.2 4.3 4.2 4.2 0 004.2 4.2 4.2 4.2 0 004.2-4.2V10a5.5 5.5 0 003.2 1V8.8a3.5 3.5 0 01-.1-.6z" fill="#FE2C55" transform="translate(-0.4 -0.3)"/>
                  <path d="M16.9 8.2a3.5 3.5 0 01-2.1-.7 3.5 3.5 0 01-1.2-1.8h-2v9.1a2.1 2.1 0 01-2.1 2 2.1 2.1 0 01-2.1-2.1 2.1 2.1 0 012.1-2.1c.2 0 .5 0 .7.1V10.5a4.3 4.3 0 00-.7-.1 4.2 4.2 0 00-4.2 4.3 4.2 4.2 0 004.2 4.2 4.2 4.2 0 004.2-4.2V10a5.5 5.5 0 003.2 1V8.8a3.5 3.5 0 01-.1-.6z" fill="#fff" transform="translate(-0.2 -0.15)"/>
                </svg>
              </a>
              <a
                href="https://www.youtube.com/@naskideantonioli5289"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="social-youtube"
                className="transition-transform hover:scale-110"
                aria-label="YouTube"
              >
                <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
                  <rect width="24" height="24" rx="6" fill="#FF0000"/>
                  <path d="M19.6 8.3a2 2 0 00-1.4-1.4C16.9 6.5 12 6.5 12 6.5s-4.9 0-6.2.4A2 2 0 004.4 8.3 21 21 0 004 12a21 21 0 00.4 3.7 2 2 0 001.4 1.4c1.3.4 6.2.4 6.2.4s4.9 0 6.2-.4a2 2 0 001.4-1.4A21 21 0 0020 12a21 21 0 00-.4-3.7z" fill="#FF0000"/>
                  <path d="M10 15l4.5-3L10 9v6z" fill="#fff"/>
                </svg>
              </a>
            </div>
          </div>
        </div>

        {/* Copyright + VibeTech */}
        <div className="border-t border-[#1a4a2e] py-4 px-4">
          <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
            <p className="text-[#3a5a3a] text-xs">{t("footerRights")}</p>
            <div className="flex items-center gap-1.5">
              <span className="text-[#3a5a3a] text-[10px] tracking-widest uppercase">Powered & Developed by</span>
              <span className="text-[#5a7a5a] text-[10px] tracking-widest uppercase font-bold">VibeTech Studio</span>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
